import { useState, useEffect } from "react";
import { Star, Frown, Smile, MessageSquare, ExternalLink, Gift } from "lucide-react";
import { cn } from "@/lib/core/utils";

// QR Code mini visual
const QRCodeMini = ({ isActive }: { isActive: boolean }) => (
  <div className={cn(
    "w-12 h-12 bg-white rounded-lg p-1.5 transition-all duration-500 shadow-sm border border-gray-100",
    isActive && "reviews-qr-pulse"
  )}>
    <div className="w-full h-full grid grid-cols-5 gap-[2px]">
      {[
        1,1,1,0,1,
        1,0,1,1,1,
        1,1,1,0,0,
        0,1,0,1,1,
        1,0,1,1,1
      ].map((filled, i) => (
        <div
          key={i}
          className={cn(
            "rounded-[1px]",
            filled ? "bg-gray-900" : "bg-transparent"
          )}
        />
      ))}
    </div>
  </div>
);

// Star rating - all 5 stars always visible
const StarRating = ({
  rating,
  isAnimating,
}: {
  rating: number;
  isAnimating: boolean;
}) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={cn(
          "w-3 h-3 transition-all duration-300",
          star <= rating
            ? rating >= 4
              ? "text-grow fill-grow"
              : "text-orange-400 fill-orange-400"
            : "text-muted-foreground/30",
          isAnimating && star <= rating && "reviews-star-in"
        )}
        style={{
          animationDelay: isAnimating ? `${(star - 1) * 60}ms` : "0ms",
        }}
      />
    ))}
  </div>
);

// Funnel destination card - compact, always visible
const FunnelCard = ({
  type,
  isActive,
  isCompleted,
}: {
  type: "unhappy" | "happy";
  isActive: boolean;
  isCompleted: boolean;
}) => {
  const isUnhappy = type === "unhappy";
  const isHighlighted = isActive || isCompleted;

  return (
    <div
      className={cn(
        "flex-1 rounded-lg p-2 transition-all duration-300 flex flex-col items-center justify-center gap-1.5",
        isHighlighted ? "opacity-100 scale-100" : "opacity-40 scale-95",
        isUnhappy
          ? "bg-orange-500/10 border border-orange-500/30"
          : "bg-grow/10 border border-grow/30"
      )}
    >
      {/* Icon */}
      <div className={cn(
        "w-7 h-7 rounded-full flex items-center justify-center",
        isUnhappy ? "bg-orange-500/20" : "bg-grow/20"
      )}>
        {isUnhappy ? (
          <Frown className="w-4 h-4 text-orange-500" />
        ) : (
          <Smile className="w-4 h-4 text-grow" />
        )}
      </div>

      {/* Stars */}
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={cn(
              "w-2.5 h-2.5",
              isUnhappy
                ? star <= 2 ? "text-orange-400 fill-orange-400" : "text-muted-foreground/20"
                : "text-grow fill-grow"
            )}
          />
        ))}
      </div>

      {/* Action badge */}
      <div className={cn(
        "flex items-center gap-1 text-[8px] font-medium rounded-full py-0.5 px-2",
        isUnhappy
          ? "bg-orange-500/20 text-orange-600"
          : "bg-grow/20 text-grow"
      )}>
        {isUnhappy ? (
          <>
            <MessageSquare className="w-2.5 h-2.5" />
            <span>Private</span>
          </>
        ) : (
          <>
            <ExternalLink className="w-2.5 h-2.5" />
            <span>Public</span>
          </>
        )}
      </div>
    </div>
  );
};

// Action column - Feedback form / Google publish - always visible
const ActionColumn = ({
  isActive,
  isCompleted,
}: {
  isActive: boolean;
  isCompleted: boolean;
}) => {
  const isHighlighted = isActive || isCompleted;

  return (
    <div className="flex flex-col gap-2 h-full">
      {/* Unhappy: Private feedback form */}
      <div className={cn(
        "flex-1 rounded-lg p-2 bg-orange-500/5 border border-orange-500/20 flex items-center gap-2 transition-all duration-300",
        isHighlighted ? "opacity-100 scale-100" : "opacity-40 scale-95"
      )}>
        <div className="w-7 h-7 rounded-lg bg-orange-500/20 flex items-center justify-center shrink-0">
          <MessageSquare className="w-4 h-4 text-orange-500" />
        </div>
        <div className="min-w-0">
          <p className="text-[9px] font-semibold text-orange-600 truncate">Feedback</p>
          <p className="text-[7px] text-orange-500/70">Private form</p>
        </div>
      </div>

      {/* Happy: Google publish */}
      <div className={cn(
        "flex-1 rounded-lg p-2 bg-grow/5 border border-grow/20 flex items-center gap-2 transition-all duration-300",
        isHighlighted ? "opacity-100 scale-100" : "opacity-40 scale-95"
      )}>
        <div className="w-7 h-7 rounded-lg bg-white border border-gray-200 flex items-center justify-center shrink-0">
          {/* Google G icon */}
          <svg viewBox="0 0 24 24" className="w-4 h-4">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        </div>
        <div className="min-w-0">
          <p className="text-[9px] font-semibold text-grow truncate">Google</p>
          <p className="text-[7px] text-grow/70">Published</p>
        </div>
      </div>
    </div>
  );
};

// Arrow component - always visible, activates when needed
const FlowArrow = ({
  isActive,
  direction = "right",
  color = "muted",
}: {
  isActive: boolean;
  direction?: "split" | "right";
  color?: "muted" | "amber";
}) => {
  if (direction === "split") {
    return (
      <svg width="70" height="70" viewBox="0 0 70 70" className={cn(
        "transition-all duration-300 shrink-0",
        isActive ? "opacity-70" : "opacity-20"
      )}>
        <path
          d="M5,35 L25,35 Q32,35 40,20 L55,12"
          stroke="rgb(249, 115, 22)"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M5,35 L25,35 Q32,35 40,50 L55,58"
          stroke="rgb(16, 185, 129)"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
        />
        {isActive && (
          <>
            <polygon points="55,12 48,6 48,18" fill="rgb(249, 115, 22)" opacity="0.8" />
            <polygon points="55,58 48,52 48,64" fill="rgb(16, 185, 129)" opacity="0.8" />
          </>
        )}
      </svg>
    );
  }

  const strokeColor = color === "amber" ? "rgb(245, 158, 11)" : "currentColor";

  return (
    <svg width="20" height="24" viewBox="0 0 20 24" className={cn(
      "transition-all duration-300 shrink-0",
      isActive ? "opacity-60" : "opacity-20",
      color === "muted" && "text-muted-foreground"
    )}>
      <path d="M2,12 L12,12" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" fill="none" />
      <polygon points="12,12 6,7 6,17" fill={strokeColor} />
    </svg>
  );
};

interface ReviewsVisualizationProps {
  isHovering: boolean;
}

// Main component - ALL STEPS VISIBLE, activate progressively
export const ReviewsVisualization = ({ isHovering }: ReviewsVisualizationProps) => {
  const [currentStep, setCurrentStep] = useState(0);

  // Animation sequence:
  // 0: Idle - all visible but dimmed
  // 1: Phone active (scanning)
  // 2: Rating active
  // 3: Funnel active
  // 4: Action active
  // 5: Reward active
  useEffect(() => {
    if (!isHovering) {
      setCurrentStep(0);
      return;
    }

    const durations = [300, 600, 900, 1000, 1200, 2500];
    const timer = setTimeout(() => {
      setCurrentStep((prev) => (prev < 5 ? prev + 1 : 0));
    }, durations[currentStep]);

    return () => clearTimeout(timer);
  }, [isHovering, currentStep]);

  // Step states
  const phoneActive = currentStep >= 1;
  const phoneCompleted = currentStep >= 2;
  const ratingActive = currentStep === 2;
  const funnelActive = currentStep === 3;
  const funnelCompleted = currentStep >= 4;
  const actionActive = currentStep === 4;
  const actionCompleted = currentStep >= 5;
  const rewardActive = currentStep === 5;
  const rewardCompleted = currentStep > 5;

  return (
    <div className="relative w-full h-full overflow-hidden p-4">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 via-transparent to-amber-500/5" />

      {/* ALL STEPS VISIBLE - HORIZONTAL LAYOUT - FULL WIDTH */}
      <div className="relative h-full flex items-center justify-between gap-2">

        {/* COL 1: Phone with QR / Rating - always visible */}
        <div className={cn(
          "flex flex-col items-center justify-center transition-all duration-300",
          phoneActive || phoneCompleted ? "opacity-100 scale-100" : "opacity-50 scale-95"
        )}>
          <div className="relative rounded-2xl bg-gray-900 p-1 shadow-lg transition-all duration-300 w-[72px] h-[110px]">
            {/* Notch */}
            <div className="absolute top-1 left-1/2 -translate-x-1/2 w-8 h-1.5 bg-gray-800 rounded-full" />
            {/* Screen */}
            <div className="w-full h-full rounded-xl bg-white flex flex-col items-center justify-center overflow-hidden relative">
              {!phoneCompleted ? (
                <>
                  <QRCodeMini isActive={phoneActive && currentStep === 1} />
                  <p className="text-[7px] text-gray-500 mt-1.5 font-medium">Scan QR</p>
                  {phoneActive && currentStep === 1 && (
                    <div className="absolute inset-x-2 top-1/2 reviews-scan-line">
                      <div className="h-0.5 bg-grow rounded-full shadow-lg shadow-grow/50" />
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center">
                  <p className="text-[7px] text-gray-500 mb-1.5 font-medium">Rate us!</p>
                  <StarRating rating={5} isAnimating={ratingActive} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Arrow 1: Split */}
        <FlowArrow isActive={funnelActive || funnelCompleted} direction="split" />

        {/* COL 2: Funnel cards - always visible - grows to fill space */}
        <div className="flex-1 max-w-[120px] flex flex-col gap-2">
          <FunnelCard type="unhappy" isActive={funnelActive} isCompleted={funnelCompleted} />
          <FunnelCard type="happy" isActive={funnelActive} isCompleted={funnelCompleted} />
        </div>

        {/* Arrow 2: To action */}
        <FlowArrow isActive={actionActive || actionCompleted} direction="right" />

        {/* COL 3: Action column - always visible - grows to fill space */}
        <div className="flex-1 max-w-[130px] h-full py-1">
          <ActionColumn isActive={actionActive} isCompleted={actionCompleted} />
        </div>

        {/* Arrow 3: To reward */}
        <FlowArrow isActive={rewardActive || rewardCompleted} direction="right" color="amber" />

        {/* COL 4: Reward - always visible */}
        <div className={cn(
          "flex flex-col items-center justify-center gap-2 px-4 transition-all duration-300",
          (rewardActive || rewardCompleted) ? "opacity-100 scale-100" : "opacity-40 scale-95"
        )}>
          {/* Reward icon */}
          <div className={cn(
            "w-14 h-14 rounded-2xl bg-amber-500/20 border-2 border-amber-500/30 flex items-center justify-center transition-all duration-300",
            rewardActive && "reviews-reward-pop"
          )}>
            <Gift className="w-7 h-7 text-amber-500" />
          </div>

          {/* Reward text */}
          <div className="text-center">
            <p className="text-[11px] font-bold text-amber-600">Reward</p>
            <p className="text-[9px] text-amber-500/80">10% OFF</p>
          </div>
        </div>
      </div>
    </div>
  );
};
